/* eslint-disable consistent-return */

import { ReduxEmitter } from '../helpers/reduxEmitter';
import { otEventManager } from '../helpers/eventManager';
import {
  OT_EVENTS,
  PUB_KEY,
  SCREEN_SHARING_VIDEO_TYPE,
} from '../helpers/constants';
import { parseUserPayload, SpeakerDetection } from '../helpers/utils';
import { emitNotification, NOTIFICATION_TYPES } from '../helpers/notifications';

export class OTSession extends ReduxEmitter {
  constructor({ config, dispatch }) {
    super(dispatch);
    this.sessionID = config.sessionID;
    this.token = config.token;
    this.apiKey = config.apiKey;
    this.session = OT.initSession(this.apiKey, this.sessionID);
    this.publisherStreamId = '';
  }

  addSubscriber(stream) {
    if (!this.session) throw new Error('No Session');

    const { streamId: id } = stream;

    const subscriber = this.session.subscribe(stream, '', {
      insertDefaultUI: false,
    });

    subscriber.on(OT_EVENTS.VIDEO_DISABLED_WARNING, e => {
      console.warn(OT_EVENTS.VIDEO_DISABLED_WARNING, e);
      if (e.target.stream) {
        this.dispatchAddVideoWarningId({ id: e.target.stream.streamId });
      }
    });

    subscriber.on(OT_EVENTS.VIDEO_DISABLED_WARNING_LIFTED, e => {
      console.warn(OT_EVENTS.VIDEO_DISABLED_WARNING_LIFTED, e);
      if (e.target.stream) {
        this.dispatchRemoveVideoWarningId({ id: e.target.stream.streamId });
      }
    });

    subscriber.once(
      OT_EVENTS.VIDEO_ELEMENT_CREATED,
      ({ target: entity, element: video }) => {
        this.dispatchUpdateConnection({
          ready: true,
          entity,
          video,
          id,
        });
      },
    );

    SpeakerDetection(
      subscriber,
      e => this.dispatchAddActiveSpeaker(e.streamId),
      e => this.dispatchRemoveActiveSpeaker(e.streamId),
    );
  }

  handleStreamCreate = ({ stream }) => {
    if (!this.session) throw new Error('No Session');

    const { streamId: id, connection } = stream;
    const user = parseUserPayload(connection.data);
    emitNotification({ type: NOTIFICATION_TYPES.JOIN, name: user.name });
    this.dispatchAddConnection({ id, stream, ready: false, user });
    this.addSubscriber(stream);
  };

  handleStreamDestroy = ({ stream }) => {
    const id = stream.streamId;
    this.dispatchRemoveConnection({ id });
    this.dispatchRemoveVideoWarningId({ id });

    if (stream.videoType === SCREEN_SHARING_VIDEO_TYPE) {
      this.dispatchFindScreenSharing(); // if exist other screen sharing set it active
    }
  };

  handleStreamPropChange = ({ stream }) => {
    const id = this.getStreamId(stream);
    this.dispatchUpdateConnection({ id, stream });
  };

  setupListeners() {
    if (!this.session) {
      console.warn('[ot-session] |setupListeners| no session ');
      return;
    }
    console.log('[ot-session] setup listeners');
    this.session.on(OT_EVENTS.STREAM_CREATED, this.handleStreamCreate);
    this.session.on(OT_EVENTS.STREAM_DESTROYED, this.handleStreamDestroy);
    this.session.on(
      OT_EVENTS.STREAM_PROPERTY_CHANGE,
      this.handleStreamPropChange,
    );
    this.session.on(OT_EVENTS.SESSION_DISCONNECTED, event => {
      if (event.reason === 'networkDisconnected') {
        otEventManager.emitRestart();
      }

      console.warn(
        '[ot-session] SESSION DISCONNECTED: todo: reconnect to session',
        event.reason,
        event,
      );
    });
  }

  clearListeners() {
    if (!this.session) {
      console.warn('[ot-session] |clearListeners| no session ');
      return;
    }
    console.log('[ot-session] clear listeners');
    this.session.off(OT_EVENTS.STREAM_CREATED, this.handleStreamCreate);
    this.session.off(OT_EVENTS.STREAM_DESTROYED, this.handleStreamDestroy);
    this.session.off(
      OT_EVENTS.STREAM_PROPERTY_CHANGE,
      this.handleStreamPropChange,
    );
    this.session.off(OT_EVENTS.SESSION_DISCONNECTED);
  }

  setPublisherStreamId(streamId) {
    this.publisherStreamId = streamId;
  }

  setTargetStreamIdOnSessionInit() {
    if (!this.session) {
      console.warn('[ot-session] |setupListeners| no session ');
      return;
    }
    const { streams } = this.session;
    const targetId =
      streams
        .map(({ id }) => id)
        .filter(id => id !== this.publisherStreamId)[0] || PUB_KEY;

    this.dispatchSetActiveId(targetId);
  }

  getStreamId({ streamId }) {
    return streamId === this.publisherStreamId ? PUB_KEY : streamId;
  }

  connect() {
    return new Promise((resolve, reject) => {
      if (!this.session) return reject(new Error('No Session'));
      console.time('connect-to-session');
      this.session.connect(this.token, error => {
        if (error) {
          console.timeEnd('connect-to-session');
          console.error('[ot-session] connect', error);

          return reject(error);
        }
        console.timeEnd('connect-to-session');
        console.log('[ot-session] successfully connected');
        resolve();
      });
    });
  }

  init() {
    this.setupListeners();
  }

  destroy() {
    this.clearListeners();
    this.session.disconnect();
    console.log('[ot-session] destroyed');
  }
}



// WEBPACK FOOTER //
// ./src/redux/opentok/core/session.js